/**
 * 租户管理 API
 */

import httpRequest from '@/utils/api'
import type { ResponseModel } from '@/types/axios/model'
import type {
  AdminTenantSupportPreviewRequest,
  AdminTenantSupportPreviewResponse,
  AdminTenantConfig,
  ConfirmTenantSettlementRequest,
  ConfirmTenantSettlementResponse,
  CreateTenantRequest,
  TenantFaviconUploadResponse,
  TenantLogoUploadResponse,
  TenantLogoVariant,
  TenantSettlementBatchDetailResponse,
  TenantSettlementBatchListResponse,
  TenantSettlementOverviewResponse,
  TenantListParams,
  TenantListResponse,
  TenantSettlementPreviewRequest,
  TenantSettlementPreviewResponse,
  UpdateTenantRequest,
  UpdateSubdomainRequest,
} from '@/types/admin/tenant'

enum API {
  /** 租户列表 */
  tenants = '/admin/tenants',
  /** 单个租户 */
  tenant = '/admin/tenants/:tenantId',
  /** 检查子域名可用性 */
  checkSubdomain = '/admin/tenants/check-subdomain',
  /** 更新子域名 */
  updateSubdomain = '/admin/tenants/:tenantId/subdomain',
  /** 上传租户 Logo */
  uploadLogo = '/admin/tenants/:tenantId/logo/:variant',
  /** 上传租户 Favicon */
  uploadFavicon = '/admin/tenants/:tenantId/favicon',
  /** 预览在线客服链接 */
  supportLinkPreview = '/admin/tenants/support-link-preview',
  /** 租户月结总览 */
  tenantSettlementOverview = '/admin/tenant-settlements/overview',
  /** 租户月结预览 */
  tenantSettlementPreview = '/admin/tenant-settlements/preview',
  /** 租户月结确认 */
  tenantSettlementConfirm = '/admin/tenant-settlements/confirm',
  /** 租户月结批次列表 */
  tenantSettlementBatches = '/admin/tenant-settlements/batches',
  /** 租户月结批次详情 */
  tenantSettlementBatch = '/admin/tenant-settlements/batches/:batchId',
}

const parseNumberField = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return fallback
}

/**
 * 获取租户列表
 *
 * @param params 查询参数
 * @returns 租户列表响应
 */
export const getTenantsAPI = (params?: TenantListParams) => {
  return httpRequest.get<TenantListResponse>({
    url: API.tenants,
    params: {
      page: params?.page,
      limit: params?.limit,
    },
  })
}

/**
 * 获取租户详情
 *
 * @param tenantId 租户 ID
 * @returns 租户详情响应
 */
export const getTenantAPI = (tenantId: number) => {
  return httpRequest.get<AdminTenantConfig>({
    url: API.tenant.replace(':tenantId', tenantId.toString()),
  })
}

/**
 * 创建租户
 *
 * @param data 创建请求数据
 * @returns 租户详情响应
 */
export const createTenantAPI = (data: CreateTenantRequest) => {
  return httpRequest.post<AdminTenantConfig>({
    url: API.tenants,
    data,
    encrypt: true,
  })
}

/**
 * 更新租户
 *
 * @param tenantId 租户 ID
 * @param data 更新请求数据
 * @returns 租户详情响应
 */
export const updateTenantAPI = (tenantId: number, data: UpdateTenantRequest) => {
  return httpRequest.put<AdminTenantConfig>({
    url: API.tenant.replace(':tenantId', tenantId.toString()),
    data,
    encrypt: true,
  })
}

/**
 * 删除租户
 *
 * @param tenantId 租户 ID
 * @returns 删除响应
 */
export const deleteTenantAPI = (tenantId: number) => {
  return httpRequest.delete({
    url: API.tenant.replace(':tenantId', tenantId.toString()),
  })
}

/**
 * 检查子域名可用性
 *
 * @param subdomain 子域名后缀
 * @param excludeTenantId 排除的租户 ID（编辑时传入当前租户 ID）
 * @returns 可用性检查响应
 */
export const checkSubdomainAPI = (subdomain: string, excludeTenantId?: number) => {
  return httpRequest.get<{ available: boolean; reason?: string; message: string }>({
    url: API.checkSubdomain,
    params: { 
      subdomain,
      exclude_tenant_id: excludeTenantId,
    },
  })
}

/**
 * 更新租户子域名
 *
 * @param tenantId 租户 ID
 * @param data 更新子域名请求数据
 * @returns 租户详情响应
 */
export const updateSubdomainAPI = (tenantId: number, data: UpdateSubdomainRequest) => {
  return httpRequest.patch<AdminTenantConfig>({
    url: API.updateSubdomain.replace(':tenantId', tenantId.toString()),
    data,
  })
}

/**
 * 上传租户 Logo。
 *
 * @param tenantId 租户 ID
 * @param file Logo 文件
 * @param variant Logo 类型
 * @returns 上传结果
 */
export const uploadTenantLogoAPI = (
  tenantId: number,
  file: File,
  variant: TenantLogoVariant,
) => {
  const formData = new FormData()
  formData.append('file', file)

  return httpRequest.post<TenantLogoUploadResponse>({
    url: API.uploadLogo
      .replace(':tenantId', tenantId.toString())
      .replace(':variant', variant),
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * 上传租户 Favicon。
 *
 * @param tenantId 租户 ID
 * @param file Favicon 文件
 * @returns 上传结果
 */
export const uploadTenantFaviconAPI = (tenantId: number, file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  return httpRequest.post<TenantFaviconUploadResponse>({
    url: API.uploadFavicon.replace(':tenantId', tenantId.toString()),
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * 预览在线客服链接。
 *
 * @param data 预览请求数据
 * @returns 预览链接响应
 */
export const previewTenantSupportLinkAPI = (data: AdminTenantSupportPreviewRequest) => {
  return httpRequest.post<AdminTenantSupportPreviewResponse>({
    url: API.supportLinkPreview,
    data,
    encrypt: true,
  })
}

/**
 * 获取租户月结总览。
 *
 * @param settlementMonth 结算月份（YYYY-MM）
 * @returns 月结总览响应
 */
export const getTenantSettlementOverviewAPI = (settlementMonth: string) => {
  return httpRequest.get<TenantSettlementOverviewResponse>({
    url: API.tenantSettlementOverview,
    params: {
      settlementMonth,
    },
  })
}

/**
 * 预览租户月结批次。
 *
 * @param data 预览请求数据
 * @returns 月结预览响应
 */
export const previewTenantSettlementAPI = (data: TenantSettlementPreviewRequest) => {
  return httpRequest.post<TenantSettlementPreviewResponse>({
    url: API.tenantSettlementPreview,
    data,
  })
}

/**
 * 确认租户月结批次。
 *
 * @param data 确认请求数据
 * @returns 月结确认响应
 */
export const confirmTenantSettlementAPI = (data: ConfirmTenantSettlementRequest) => {
  return httpRequest.post<ConfirmTenantSettlementResponse>({
    url: API.tenantSettlementConfirm,
    data,
  })
}

/**
 * 获取租户月结批次列表。
 *
 * @param params 查询参数
 * @returns 批次列表响应
 */
export const getTenantSettlementBatchesAPI = (params: {
  tenantId?: number | null
  settlementMonth?: string | null
  limit?: number
  offset?: number
}) => {
  return httpRequest.get<TenantSettlementBatchListResponse>({
    url: API.tenantSettlementBatches,
    params,
  })
}

/**
 * 获取租户月结批次详情。
 *
 * @param batchId 批次 ID
 * @returns 批次详情响应
 */
export const getTenantSettlementBatchDetailAPI = (batchId: number) => {
  return httpRequest.get<TenantSettlementBatchDetailResponse>({
    url: API.tenantSettlementBatch.replace(':batchId', batchId.toString()),
  })
}
